import { apiSlice } from '../../../apis/api';
import { CalculationsCollection } from '../../../swagger/collections';
import {
  CalculationStatisticRequestListItemResponse,
  CalculationStatisticRequestListItemSourceResponse,
  CalculationStatisticRequestListSummaryResponse,
} from '@ydistri/api-sdk';
import { getTags } from '../../../apis/apiLib';
import { computePercentage } from '@ydistri/generic-utils';

const { TAGS, TAGS_ARRAY } = getTags('calculationStatistics');

interface GetSourceItemsForTargetSkuRequest {
  calculationId: number;
  targetSkuId: number;
}

export interface CalculationStatisticRequestListResponseWithKey extends CalculationStatisticRequestListItemResponse {
  key: number;
  receivedQuantityPercentage: number;
}

const countPercentage = (data: CalculationStatisticRequestListItemResponse): number =>
  Math.floor(computePercentage(data.receivedQuantity, data.requestedQuantity));

export const apiCalculationRequestListStatistics = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getRequestListOverallData: builder.query<
        CalculationStatisticRequestListSummaryResponse,
        number
      >({
        queryFn: async calculationId => {
          const { data } =
            await CalculationsCollection.getCalculationStatisticRequestListSummary(calculationId);
          return data;
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.calculationStats, id: `overall-${arg}` },
        ],
      }),
      getRequestListItems: builder.query<CalculationStatisticRequestListResponseWithKey[], number>({
        queryFn: async calculationId => {
          const { data } =
            await CalculationsCollection.getCalculationStatisticRequestListItems(calculationId);
          const mappedData = data.data
            .map(
              (item): CalculationStatisticRequestListResponseWithKey => ({
                ...item,
                key: item.skuId,
                receivedQuantityPercentage: countPercentage(item),
              }),
            )
            .sort((left, right) => {
              if (left.receivedQuantityPercentage === right.receivedQuantityPercentage) {
                const leftDiff = left.requestedQuantity - left.receivedQuantity;
                const rightDiff = right.requestedQuantity - right.receivedQuantity;
                return rightDiff - leftDiff;
              } else {
                return left.receivedQuantityPercentage > right.receivedQuantityPercentage ? -1 : 1;
              }
            });

          return { data: mappedData };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.calculationStats, id: `target-${arg}` },
        ],
      }),
      getSourceItemsForTargetSku: builder.query<
        CalculationStatisticRequestListItemSourceResponse[],
        GetSourceItemsForTargetSkuRequest
      >({
        queryFn: async ({ calculationId, targetSkuId }) => {
          const { data } =
            await CalculationsCollection.getCalculationStatisticRequestListItemSources(
              calculationId,
              targetSkuId,
            );

          return data;
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.calculationStats, id: `sources-${arg.targetSkuId}` },
        ],
      }),
    }),
  });

export const {
  useGetSourceItemsForTargetSkuQuery,
  useGetRequestListOverallDataQuery,
  useGetRequestListItemsQuery,
} = apiCalculationRequestListStatistics;
